import kUserServiceSdk from 'shared-assets/lib/sdk/kUserService';
import { CQM_SOLUTION_LOGOUT_URL } from 'clinical-reporting-ui/constants/apis';

angular
  .module('kareo.ui.shared.modules.api.user.k-user-service', [
    'kareo.ui.shared.modules.authentication.k-roles'
  ])
  .factory('kUserService', kUserService);

const PASSWORD_MASK = '';
const USERS_CACHE = {};

kUserService.$inject = ['$cacheFactory', '$http', '$q', 'K_ROLES', '$rootScope'];
function kUserService($cacheFactory, $http, $q, K_ROLES, $rootScope) {
  const svc = {};

  const BULK_HTTP_CACHE = $cacheFactory('kUsers.bulk');
  const HTTP_CACHE = $cacheFactory('kUsers');

  svc.createUser = createUser;
  svc.deleteUser = deleteUser;
  svc.deleteUserV2 = deleteUserV2;
  svc.getCurrentUser = getCurrentUser;
  svc.getAllUsersForPractice = _getAllUsersForPractice();
  svc.getUsersForPracticePaged = _getUsersForPracticePaged();
  svc.hasProviderShellsPending = hasProviderShellsPending;
  svc.getNewUser = _getNewUser();
  svc.getPasswordRequirementsForCustomer = getPasswordRequirementsForCustomer;
  svc.getUserByGuid = getUserByGuid;
  svc.getUserByProviderGuid = getUserByProviderGuid;
  svc.lockUser = lockUser;
  svc.updateUser = updateUser;
  svc.updateUserV2 = updateUserV2;
  svc.claimProviderSubscription = claimProviderSubscription;
  svc.validateUniqueUserEmail = validateUniqueUserEmail;
  svc.validateUserPassword = validateUserPassword;
  svc.isRcmEmployee = isRcmEmployee;
  svc.isUserPrescriber = isUserPrescriber;
  svc.prescriberRequiresSupervision = prescriberRequiresSupervision;
  svc.updateRequiresSupervisingProvider = updateRequiresSupervisingProvider;
  svc.getAvailableProxies = getAvailableProxies;
  svc.updateProxies = updateProxies;
  svc.getPracticeProxies = getPracticeProxies;
  svc.logOutFromCqmSolution = logOutFromCqmSolution;
  svc.putUserInCache = putUserInCache;
  svc.removeUserCaches = removeUserCaches;

  return svc;

  // ------------------------------------------------------------------------

  function addIsProviderBoolean(user) {
    user = angular.copy(user);
    Object.defineProperty(user, 'isProvider', {
      configurable: true,
      enumerable: true,
      get() {
        return this.roles && this.roles.indexOf(K_ROLES.PROVIDER) >= 0;
      },
      set(val) {
        this.roles = syncRolesWithIsProvider(this.roles, !!val);
      }
    });
    return user;
  }

  function createUser(user, purchaseGuid) {
    user = updateUserRoles(user);

    return $http.post('/common-ui/api/User/', user, {
      params: {
        purchaseGuid
      }
    })
      .then(fixHttpPromise)
      .then(verifyUser)
      .then(putInHttpCache)
      .then((u) => {
        invalidateBulkCache();
        return u;
      })
      .then(putInCache)
      .then(decorateUser);
  }

  function decorateUser(user) {
    user = angular.copy(user);

    user.password = PASSWORD_MASK;
    user.practices = user.practices || [];
    user.repeatPassword = PASSWORD_MASK;
    user.roles = user.roles || [];
    user.providerInfo = user.providerInfo || {};
    user.providerInfo.products = user.providerInfo.products || {};
    user.isEmergencyAdmin = !!user.isEmergencyAdmin;

    // fixes "0" value from API
    if (!user.providerInfo.providerTypeId) {
      delete user.providerInfo.providerTypeId;
    }
    if (!user.providerInfo.specialtyId) {
      delete user.providerInfo.specialtyId;
    }

    return addIsProviderBoolean(user);
  }

  function deleteUser(userGuid) {
    return deleteUserRequest(userGuid, userUrl(userGuid));
  }

  function deleteUserV2(userGuid) {
    return deleteUserRequest(userGuid, userUrlV2(userGuid));
  }

  function deleteUserRequest(userGuid, deleteUserUrl) {
    if (!USERS_CACHE.hasOwnProperty(userGuid)) {
      return $q.reject('User not found');
    }

    return $http
      .delete(deleteUserUrl)
      .then(() => {
        removeFromCaches(userGuid);
      });
  }

  function fixHttpPromise(response) {
    return response.data;
  }

  function _getAllUsersForPractice() {
    var promise;
    return function getAllUsersForPractice() {
      if (promise) {
        return promise;
      }

      promise = $http
        .get('/common-ui/api/User/getAllUsers', {
          cache: BULK_HTTP_CACHE
        })
        .then(fixHttpPromise)
        .then((fetchedUsers) => {
          const usersArr = [];
          angular.forEach(fetchedUsers, (user) => {
            user = decorateUser(user);
            putInCacheIfMissing(user);
            usersArr.push(user);
          });

          promise = undefined;

          // this does not put users in http cache as it only fetches
          // summaries. a GET, POST, or PUT will fetch the entire user
          // object and update the cache

          return usersArr;
        });

      return promise;
    };
  }

  function _getUsersForPracticePaged(){
    let abortController = new AbortController();
    let promise;
    let resolveFunction;
    return function getUsersForPracticePaged({pageNumber, pageSize, filter}){
      if(!promise){
        promise = new Promise((resolve, reject) => {
          resolveFunction = resolve;
        });
      }
      abortController.abort();
      abortController = new AbortController();
      kUserServiceSdk.getUsersForPracticePaged({
        pageNumber,
        pageSize,
        filter,
        options: { signal: abortController.signal }
      }).then(data => {
        return {
          items: Array.isArray(data?.items) ? data.items.map( user => decorateUser(user )) : [],
          totalCount: data?.totalCount
        };
      }).then(data => {
        resolveFunction(data)
        promise = undefined;
      });

      return promise;
    }
  }

  function hasProviderShellsPending() {
    return new Promise((resolve, reject) => {
      let hasProviderShellsPending = true;
      const completedProviderShellsFlagKey = _getCompletedProviderShellsFlagKey();
  
      if (!!completedProviderShellsFlagKey) {
        hasProviderShellsPending = !localStorage.getItem(
          completedProviderShellsFlagKey,
        );
      }
  
      if (hasProviderShellsPending) {
        const getUsersForPracticePaged = _getUsersForPracticePaged();
        getUsersForPracticePaged({
          pageNumber: 0,
          // Provider shells lastname starts with { and they are all providers, that's the filter we are appling
          filter: { search: '{', showProviders: true },
        })
          .then(providerShellsResponse => {
            hasProviderShellsPending = providerShellsResponse?.totalCount > 0;
            if (!!completedProviderShellsFlagKey && !hasProviderShellsPending) {
              localStorage.setItem(completedProviderShellsFlagKey, true);
            }
            resolve(hasProviderShellsPending);
          })
          .catch(error => {
            reject(error);
          });
      } else {
        resolve(hasProviderShellsPending);
      }
    });
  }
  
  function _getCompletedProviderShellsFlagKey() {
    const currentAccountIds = sessionStorage.getItem('currentAccountIds');
    return !!currentAccountIds
      ? 'completedProviderShellsFlagKey' + currentAccountIds
      : '';
  }

  function _getNewUser() {
    const template = {
      isProvider: false,
      practices: [],
      providerInfo: {
        products: {}
      },
      roles: [],
      isEmergencyAdmin: false
    };
    return addIsProviderBoolean.bind(null, template);
  }

  function getCurrentUser() {
    return $http
      .get(userUrl('me'), {
        cache: HTTP_CACHE
      })
      .then(fixHttpPromise)
      .then(putInHttpCache) // caches by guid
      .then(putInCache)
      .then(decorateUser);
  }

  function getPasswordRequirementsForCustomer() {
    return $http
      .get('/common-ui/api/User/passwordRequirements', {
        cache: true
      })
      .then(fixHttpPromise);
  }

  async function logOutFromCqmSolution() {
    await $http.post(CQM_SOLUTION_LOGOUT_URL);
  }

  function getUserByGuid(userGuid) {
    return $http
      .get(userUrl(userGuid), {
        cache: HTTP_CACHE
      })
      .then(fixHttpPromise)
      .then(putInCache)
      .then(decorateUser);
  }

  function getUserByProviderGuid(providerGuid) {
    return $http
      .get(userUrl('provider/' + providerGuid), {
        cache: HTTP_CACHE
      })
      .then(fixHttpPromise)
      .then(putInCache)
      .then(decorateUser);
  }

  function invalidateBulkCache() {
    BULK_HTTP_CACHE.removeAll();
  }

  function lockUser(user) {
    user = angular.copy(user);
    user.isLocked = true;
    return updateUser(user);
  }

  function putInCache(user) {
    const guid = user.userGuid;
    const cached = USERS_CACHE[guid];
    if (!cached) {
      USERS_CACHE[guid] = user;
    } else if (cached !== user) {
      angular.copy(user, USERS_CACHE[guid]);
    }
    return user;
  }

  function putInCacheIfMissing(user) {
    if (!USERS_CACHE.hasOwnProperty(user.userGuid)) {
      putInCache(user);
    }
    return user;
  }

  function putInHttpCache(user) {
    HTTP_CACHE.put(userUrl(user), user);
    HTTP_CACHE.put(userUrlV2(user), user);
    return user;
  }

  function putUserInCache(user) {
    putInCache(user);
    putInHttpCache(user);
  }

  function removeUserCaches(userGuid, isCurrentUser) {
    if(isCurrentUser) {
      HTTP_CACHE.remove(userUrl('me'));
    }
    HTTP_CACHE.remove(userUrlV2(userGuid));
    removeFromCaches(userGuid);
  }

  function removeFromCaches(userGuid) {
    delete USERS_CACHE[userGuid];
    HTTP_CACHE.remove(userUrl(userGuid));
    invalidateBulkCache();
  }

  function stripPasswordMask(user) {
    user = angular.copy(user);
    if (user.password === PASSWORD_MASK &&
      user.repeatPassword === PASSWORD_MASK) {
      delete user.password;
      delete user.repeatPassword;
    }
    return user;
  }

  function syncRolesWithIsProvider(roles, isProvider) {
    roles = angular.copy(roles);
    const providerRoleIndex = roles.indexOf(K_ROLES.PROVIDER);
    const hasProviderRole = providerRoleIndex >= 0;
    if (hasProviderRole && !isProvider) {
      roles.splice(providerRoleIndex, 1);
    } else if (!hasProviderRole && isProvider) {
      roles.push(K_ROLES.PROVIDER);
    }
    return roles;
  }

  function updateUser(user, purchaseGuid) {
    return updateUserRequest(user, purchaseGuid, userUrl(user));
  }

  function updateUserV2(user, purchaseGuid) {
    return updateUserRequest(user, purchaseGuid, userUrlV2(user));
  }

  function updateUserRequest(user, purchaseGuid, updateUserUrl) {
    user = stripPasswordMask(user);
    user = updateUserRoles(user);

    return $http.put(updateUserUrl, user, {
      params: {
        purchaseGuid
      }
    })
      .then(fixHttpPromise)
      .then(verifyUser)
      .then(putInHttpCache)
      .then(putInCache)
      .then((u) => {
        invalidateBulkCache();
        return u;
      })
      .then(decorateUser);
  }

  function updateUserRoles(user) {
    // this makes sure that roles are in sync with isProvider at time of save
    // (ex if consumer angular.copies user and loses the custom accessor)
    user = angular.copy(user);
    user.roles = syncRolesWithIsProvider(user.roles, user.isProvider);
    return user;
  }

  function userUrl(userOrRelativePath) {
    const relativePath = angular.isObject(userOrRelativePath) ?
                         userOrRelativePath.userGuid :
                         userOrRelativePath;
    return '/common-ui/api/User/' + relativePath;
  }

  function userUrlV2(userOrRelativePath) {
    const relativePath = angular.isObject(userOrRelativePath) ?
                         userOrRelativePath.userGuid :
                         userOrRelativePath;
    return '/common-ui/api/User/v2/' + relativePath;
  }

  function verifyUser(user) {
    if (!user || !user.userGuid) {
      throw new Error('Unable to update user');
    }
    return user;
  }

  function validateUniqueUserEmail(email) {
    return $http
      .get('/common-ui/api/User/email/' + email, {
        cache: true
      })
     .then(fixHttpPromise)
      .then((response) => {
        return response ? $q.when(true) : $q.reject(false);
      });
  }

  function validateUserPassword(password) {
    return $http
      .post('/common-ui/api/User/password', {
        password
      })
      .then(fixHttpPromise, (response) => {
        var messages;
        if (response.data) {
          messages = JSON.parse(response.data.message);
        }
        messages = messages || [];
        return $q.reject(messages);
      });
  }
  function isRcmEmployee() {
    return $q( (resolve, reject) => {
      return kUserServiceSdk.isRevenueCycleManagementEmployee()
          .then((response) => $rootScope.$apply(resolve(response)), (response) => $rootScope.$apply(reject(response)));
    })
  }

  function isUserPrescriber(userGuid) {
    let url = '/admin-ui/api/User/' + userGuid + '/isprescriber';
    return $http.get(url)
      .then((response) => {
        return response.data;
      });
  }

  function prescriberRequiresSupervision(userGuid) {
    let url = '/admin-ui/api/User/' + userGuid + '/prescriber/requiressupervision';
    return $http.get(url)
      .then((response) => {
        return JSON.parse(response.data);
      });
  }

  function updateRequiresSupervisingProvider(userGuid, requiresSupervisingProvider) {
    let url = '/admin-ui/api/User/prescriber/requiressupervision';
    let request = {
      userGuid: userGuid,
      requiresSupervision: requiresSupervisingProvider
    };

    return $http.put(url, request)
      .then((response) => {
        if (response.status === 204) {
          return true;
        } else {
          return false;
        }
      });
  }

  function getAvailableProxies() {
      let url = "/medications-ui/api/erx-proxy/";
      return $http.get(url)
          .then((response) => {
              return response.data;
          });
  }

  function updateProxies(proxies) {
      let url = "/medications-ui/api/erx-proxy/assign";

      let request = [];
      proxies.forEach(function(proxy) {
          let requestProxy = {};
          requestProxy.prescriberUserGuid = proxy.prescriberUserGuid;
          requestProxy.proxyUserGuid = proxy.proxyUserGuid;
          requestProxy.active = proxy.active;
          request.push(requestProxy);
      });

      return $http.put(url, request)
          .then((response) => {
              return response.status >= 200 && response.status < 300
          });
  }

  function getPracticeProxies() {
      let url = "/medications-ui/api/erx-proxy/practice";
      return $http.get(url)
          .then((response) => {
              return response.data;
          });
  }

  function claimProviderSubscription(userGuid, providerGuid, claimingUserId, claimingUserGuid) {
    if(!userGuid || !providerGuid || !claimingUserId || !claimingUserGuid) {
      console.error(`Transfer Provider User Shell missing guids: ${userGuid}, ${providerGuid}, ${claimingUserId} or ${claimingUserGuid}`);
      return new Promise((resolve) => { resolve(false); });
    }
    const url = "/common-ui/api/User/transferProviderUserShell";
    const body = {"userGuid": userGuid, "providerGuid": providerGuid, "claimingUserId": claimingUserId, "claimingUserGuid": claimingUserGuid};
    return $http.post(url, body);
  }

}
